import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@wisewit.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back, Admin.");
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink text-bone flex">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 border-r border-hairline p-12 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <Link to="/" className="relative flex items-center gap-3 w-fit" data-testid="login-back-home">
          <div className="w-8 h-8 bg-safety flex items-center justify-center">
            <span className="font-display font-black text-white text-sm">L</span>
          </div>
          <span className="font-display font-bold text-lg tracking-tightest">Lingent</span>
        </Link>
        <div className="relative">
          <div className="label-mono text-safety mb-4">// admin console</div>
          <h1 className="font-display text-5xl xl:text-6xl font-black tracking-tightest leading-[1]">
            Sign in to draft<br />tomorrow's story.
          </h1>
          <p className="mt-6 max-w-md text-bone/60 leading-relaxed">
            The Lingent floating icon will appear once you're in. Drag it wherever you
            want — your composer follows.
          </p>
        </div>
        <div className="relative label-mono text-bone/40">
          WISEWIT · INDUSTRIAL AI · 2026
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden inline-flex items-center gap-2 label-mono text-bone/60 mb-8" data-testid="login-back-mobile">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <div className="label-mono text-safety">// login</div>
          <h2 className="font-display text-4xl font-black tracking-tightest mt-2">Admin Access</h2>
          <p className="mt-3 text-bone/60">Only the WiseWit admin can talk to Lingent.</p>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="label-mono text-bone/70">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein h-12"
                placeholder="admin@wisewit.com"
                required
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="label-mono text-bone/70">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein h-12"
                placeholder="••••••••"
                required
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-none bg-klein hover:bg-klein-hover text-white label-mono h-12"
              data-testid="login-submit-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enter Console"}
            </Button>
          </form>

          <div className="mt-10 border border-hairline p-4 text-xs text-bone/50">
            <div className="label-mono text-bone/40 mb-1">Demo credentials</div>
            admin@wisewit.com · Wisewit@2026
          </div>
        </div>
      </div>
    </div>
  );
}
